import type { Schema } from '@cli-types/core-engine/schema.js'
import type { DevModeSnippet } from '@cli-types/next-adapter/generators.js'
import { SINGLE_ACTION_FIELD_EXCLUSIONS } from '@cli-constants/generators.js'
import { singularize, toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { renderBuildSingleDevModeSnippetsRenderer } from '@next-adapter/templates/renderers/generators/dev-mode-integration/build-single-dev-mode-snippets.js'
import { createInputObject } from './create-input-object.js'
import { createPayloadExample } from './create-payload-example.js'
import { createResponseExample } from './create-response-example.js'
import { createSingleDataObject } from './create-single-data-object.js'
import { formatTsObject } from './format-ts-object.js'
import { isActionField } from './is-action-field.js'
import { resolveActionModulePath } from './resolve-action-module-path.js'
export function buildSingleDevModeSnippets(
  schema: Schema,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeSnippet[] {
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)
  const camelSingular = toCamelCase(singular)
  const singleLabel = schema.label
  const inputFields = flattenFields(schema.fields)
    .filter((field) => !(field.type === 'relationship' && field.multiple === true))
    .filter((field) => isActionField(field, SINGLE_ACTION_FIELD_EXCLUSIONS))
  const upsertInput = createInputObject(inputFields)
  const actionModulePath = resolveActionModulePath(actionImportPath, adminImportAlias)
  const singleData = createSingleDataObject(schema)
  const upsertResponse = { success: true, [camelSingular]: singleData }

  return [
    {
      id: 'read',
      label: `${singleLabel} Read`,
      kind: 'query',
      icon: 'eye',
      language: 'tsx',
      responseExample: createResponseExample(singleData),
      code: renderBuildSingleDevModeSnippetsRenderer({
        kind: 'renderBuildSingleDevModeSnippetsImportsTemplate',
        props: {
          singular: Singular,
          actionModulePath: actionModulePath,
          camelSingular: camelSingular
        }
      })
    },
    {
      id: 'upsert',
      label: `Save ${singleLabel}`,
      kind: 'mutation',
      icon: 'save',
      language: 'tsx',
      payloadExample: createPayloadExample(upsertInput),
      responseExample: createResponseExample(upsertResponse),
      code: renderBuildSingleDevModeSnippetsRenderer({
        kind: 'renderBuildSingleDevModeSnippetsClientModuleTemplate',
        props: {
          singular: Singular,
          actionModulePath: actionModulePath,
          tsObject: formatTsObject(upsertInput),
          toLowerCase: schema.label.toLowerCase()
        }
      })
    }
  ]
}
